import { Injectable, NgModule } from '@angular/core';
import { Routes, RouterModule, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { AppIdGuardService, AuthenticationGuardService } from '@nx-ahs/shared/guards';
import { UploadUsersComponent } from './modules/admin/upload-users/upload-users.component';
import { DownloadUsersComponent } from './modules/admin/download-users/download-users.component';
import { DownloadRolesComponent } from './modules/admin/download-roles/download-roles.component';
import { DownloadPermissionsComponent } from './modules/admin/download-permissions/download-permissions.component';
import { PrivacyComponent } from './core/components/privacy/privacy.component';
import { TermsOfServiceComponent } from './core/components/terms-of-service/terms-of-service.component';
import { BaseAhsControlsDemoComponent } from '@nx-ahs/base-ahs-controls/demo';
import { SearchUserStateComponent } from '@nx-ahs/search';
import { DocumentDetailsPageComponent } from './modules/document/document-details-page/document-details-page.component';
import { ApplicationDetailsSidebarComponent } from '@nx-ahs/application';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';

const routes: Routes = [
	{
		path: '',
		loadComponent: () => import('./modules/home/home-page/home-page.component').then((c) => c.HomePageComponent),
		canActivate: [AuthenticationGuardService],
		title: 'Dashboard'
	},
	{
		path: 'announcements',
		loadChildren: () => import('./modules/announcements/announcements.routes').then((r) => r.ANNOUNCEMENTS_ROUTES),
		title: 'Announcements'
	},
	{
		path: 'document',
		loadChildren: () => import('./modules/document/document.routes').then((r) => r.DOCUMENT_ROUTES),
		title: 'Document'
	},
	{
		path: 'history',
		children: [
			{
				path: 'source-entity-history/:sourceEntityNameId/:sourceEntityId/:title',
				loadComponent: () =>
					import('./modules/history/history-item-page/history-item-page.component').then(
						(c) => c.HistoryItemPageComponent
					),
				canActivate: [AuthenticationGuardService, AppIdGuardService],
				title: 'History'
			},
			{
				path: 'source-entity-history/:sourceEntityNameId/:sourceEntityId/:title/:accountTypeId/:accountId',
				loadComponent: () =>
					import('./modules/history/history-item-page/history-item-page.component').then(
						(c) => c.HistoryItemPageComponent
					),
				canActivate: [AuthenticationGuardService, AppIdGuardService],
				title: 'History'
			}
		]
	},
	{
		path: 'security',
		loadChildren: () => import('./modules/security/security.routes').then((r) => r.SECURITY_ROUTES)
	},
	{
		path: 'script-builder',
		loadChildren: () =>
			import('./modules/lazy/dynamic-scripting/dynamic-scripting-lazy.module').then(
				(m) => m.DynamicScriptingLazyModule
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 1 },
		title: 'Script Builder'
	},
	{
		path: 'base-ahs-controls-demo',
		component: BaseAhsControlsDemoComponent,
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 1 }
	},
	{
		path: 'account',
		loadChildren: () => import('./modules/account/account.routes').then((m) => m.ACCOUNT_ROUTES),
		title: 'Account'
	},
	{ path: 'application', loadChildren: () => import('@nx-ahs/application').then((m) => m.ApplicationModule) },
	{
		path: 'application-verification',
		loadChildren: () => import('@nx-ahs/application').then((m) => m.ApplicationModule),
		title: 'Application'
	},
	{
		path: 'encounter',
		loadChildren: () => import('./modules/encounter/encounter.routes').then((r) => r.ENCOUNTER_ROUTES),
		title: 'New Call'
	},
	{
		path: 'error',
		loadChildren: () =>
			import('./modules/error-handling/error-handling.routes').then((r) => r.ERROR_HANDLING_ROUTES),
		title: 'Error'
	},
	{
		path: 'admin',
		loadChildren: () => import('./modules/admin/admin.routes').then((r) => r.ADMIN_ROUTES)
	},
	{
		path: 'task-inbox',
		loadChildren: () => import('./modules/tasks/tasks.routes').then((r) => r.TASKS_ROUTES)
	},
	{
		path: 'task-inbox/:inboxName',
		loadChildren: () => import('./modules/tasks/tasks.routes').then((r) => r.TASKS_ROUTES)
	},
	{
		path: 'task-inbox/:taskInboxId',
		loadChildren: () => import('./modules/tasks/tasks.routes').then((r) => r.TASKS_ROUTES)
	},
	{
		path: 'task-inbox/:taskInboxId/:taskQueueId',
		loadChildren: () => import('./modules/tasks/tasks.routes').then((r) => r.TASKS_ROUTES)
	},
	{
		path: 'task-inbox/:taskInboxId/:taskQueueId/:taskTypeId',
		loadChildren: () => import('./modules/tasks/tasks.routes').then((r) => r.TASKS_ROUTES)
	},
	{
		path: 'search',
		loadChildren: () => import('@nx-ahs/search').then((m) => m.SearchModule),
		title: 'Search'
	},
	{
		path: 'searches',
		loadChildren: () => import('./modules/searches/searches.routes').then((r) => r.SEARCHES_ROUTES)
	},
	{
		path: 'report',
		loadChildren: () => import('@nx-ahs/reports').then((m) => m.REPORT_ROUTES),
		title: 'Reports'
	},
	{
		path: 'messaging/conv/:conversationId',
		loadComponent: () =>
			import('./modules/messaging/messaging/messaging.component').then((c) => c.MessagingComponent),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Messaging'
	},
	{
		path: 'action-note-management',
		loadComponent: () =>
			import('./modules/encounter/components/note-management/encounter-action-note-management.component').then(
				(c) => c.EncounterActionNoteManagementComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Automated Note Management'
	},
	{
		path: 'download-permissions',
		component: DownloadPermissionsComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Permissions'
	},
	{
		path: 'download-roles',
		component: DownloadRolesComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Roles'
	},
	{
		path: 'download-users',
		component: DownloadUsersComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Users'
	},
	{
		path: 'search-user-state/:searchId/:customizeKey/:searchStateType',
		component: SearchUserStateComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{
		path: 'upload-users',
		component: UploadUsersComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 1 }
	},
	{ path: 'privacy', component: PrivacyComponent },
	{
		path: 'task-details-sidebar/:taskId',
		loadComponent: () =>
			import('./modules/tasks/task-details-sidebar/task-details-sidebar.component').then(
				(c) => c.TaskDetailsSidebarComponent
			),
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{
		path: 'application-details-sidebar/:applicationId/:entityNameId/:entityId',
		component: ApplicationDetailsSidebarComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		loadChildren: () => import('@nx-ahs/application').then((m) => m.ApplicationModule)
	},
	{
		path: 'document-details-page/:documentId',
		component: DocumentDetailsPageComponent,
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{
		path: 'mail',
		loadChildren: () => import('@nx-ahs/mail').then((m) => m.MAIL_ROUTES),
		title: 'Mail'
	},
	{
		path: 'capacity-management',
		loadComponent: () =>
			import('./modules/capacity-management/capacity-management/capacity-management.component').then(
				(c) => c.CapacityManagementComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Capacity Management'
	},
	{
		path: 'main-search',
		loadChildren: () => import('./modules/searches/searches.routes').then((r) => r.SEARCHES_ROUTES),
		title: 'Main Search'
	},
	{
		path: 'skill-management',
		loadComponent: () =>
			import('./modules/skill-management/skill-management/skill-management.component').then(
				(c) => c.SkillManagementComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Skill Management'
	},
	{
		path: 'check-management',
		loadComponent: () =>
			import('./modules/check-management/check-management/check-management.component').then(
				(c) => c.CheckManagementComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		title: 'Check Management'
	},
	{
		path: 'upload-user-skills',
		loadComponent: () =>
			import('./modules/skill-management/upload-user-skills/upload-user-skills.component').then(
				(c) => c.UploadUserSkillsComponent
			),
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{ path: 'tos', component: TermsOfServiceComponent },
	{
		path: 'tripetto',
		loadChildren: () => import('@nx-ahs/script-collector').then((m) => m.CollectorModule),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 1 }
	},
	{
		path: 'app-caddy',
		loadComponent: () =>
			import('./modules/encounter/components/caddy/caddy.component').then((c) => c.CaddyComponent),
		outlet: 'sidebar',
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{
		path: 'formly',
		loadChildren: () =>
			import('./modules/formly-custom-components/formly-lazy-wrapper.module').then(
				(m) => m.FormlyLazyWrapperModule
			)
	},
	{
		path: 'performance-management/dashboard',
		loadComponent: () =>
			import('./modules/performance-management/performance-dashboard/performance-dashboard.component').then(
				(c) => c.PerformanceDashboardComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 6327 },
		title: 'Performance Management'
	},
	{
		path: 'case-notes/dashboard',
		loadComponent: () =>
			import('./modules/notes/bulk-management/bulk-management-dashboard.component').then(
				(c) => c.BulkCaseNoteComponent
			),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 6327 },
		title: 'Bulk Case Note Management'
	},
	{
		path: 'admin/feature-flags',
		loadComponent: () => import('@nx-ahs/feature-flags').then((c) => c.FeatureFlagsComponent),
		canActivate: [AuthenticationGuardService, AppIdGuardService],
		data: { permissionId: 8328 }
	},
	{
		path: 'release-notes',
		loadChildren: () => import('@nx-ahs/release-notes').then((m) => m.RELEASE_NOTES_ROUTES),
		canActivate: [AuthenticationGuardService, AppIdGuardService]
	},
	{ path: '**', redirectTo: '/error/404' }
];

@Injectable()
export class PageTitleStrategy extends TitleStrategy {
	constructor(private readonly title: Title) {
		super();
	}

	override updateTitle(routerState: RouterStateSnapshot) {
		let route = routerState.root;
		let browserTitle = '';

		//loop through each level of the route and see if its child has a title, and if so, append to the end of the title
		while (route.firstChild) {
			route = route.firstChild;
			if (route.routeConfig?.title) {
				browserTitle = `${browserTitle} - ${route.routeConfig.title}`;
			}
		}

		if (routerState.root.children.length > 1) {
			let rootChild: any;
			if (routerState.root.children.find((c) => c.routeConfig?.path == 'task-details-sidebar/:taskId')) {
				rootChild = routerState.root.children.find(
					(c) => c.routeConfig?.path == 'task-details-sidebar/:taskId'
				);
				browserTitle += ` - Task ${rootChild.params.taskId} - Details`;
			}
		}

		if (browserTitle != '') {
			let browserTitlePrefix = 'HT';
			if (environment.environmentName != 'Production' && environment.environmentName != 'Prod') {
				browserTitlePrefix = 'HT ' + environment.environmentName;
			}

			this.title.setTitle(browserTitlePrefix + browserTitle);
		}
	}
}

@NgModule({
	imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled' })],
	exports: [RouterModule],
	providers: [
		{
			provide: TitleStrategy,
			useClass: PageTitleStrategy
		}
	]
})
export class AppRoutingModule {}
